import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import BlockActorDialog from "./BlockActorDialog";
import {
  ShieldOff, Eye, Plus, Trash2, ChevronDown, ChevronRight, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Collapsible panel showing all currently-active block/watch entries. Renders
 * below the 4 category cards on /admin/security-events. Two sub-tables
 * (blocks + watches) so the "what's currently rejecting requests" view is
 * immediately obvious.
 */
export default function ActorListsPanel() {
  const [open, setOpen] = useState(true);
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ total: 0, block: 0, watch: 0 });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {id, label}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/actor-lists");
      setEntries(data.items || []);
      setCounts({ total: data.count || 0, block: data.block_count || 0, watch: data.watch_count || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load actor lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doRemove = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/actor-lists/${confirm.id}`);
      toast.success(`Removed ${confirm.label}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const blocks = entries.filter((e) => e.kind === "block");
  const watches = entries.filter((e) => e.kind === "watch");

  return (
    <Card className="mt-6 p-0 overflow-hidden border-slate-200 shadow-sm" data-testid="actor-lists-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition"
        data-testid="actor-lists-toggle-btn"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} className="text-slate-500"/> : <ChevronRight size={16} className="text-slate-500"/>}
          <ShieldOff size={16} className="text-brand-primary"/>
          <span className="text-sm font-semibold text-slate-800">Blocked &amp; watched actors</span>
          <span className="text-xs text-slate-500 ml-1">
            ({counts.block} block{counts.block !== 1 ? "s" : ""}, {counts.watch} watch{counts.watch !== 1 ? "es" : ""})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            onClick={(e) => { e.stopPropagation(); setAddOpen(true); }}
            className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline"
            data-testid="actor-lists-add-btn"
            role="button"
          >
            <Plus size={12}/>Add manually
          </span>
          <span
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            data-testid="actor-lists-refresh-btn"
            role="button"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""}/>
          </span>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-0 border-t border-slate-100" data-testid="actor-lists-body">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
              <Loader2 className="animate-spin" size={14}/>Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="actor-lists-empty">
              No actors are currently blocked or watched. Use the Block / Watch buttons on the recent-event rows above, or click <span className="font-medium">Add manually</span> to seed one.
            </div>
          ) : (
            <div className="mt-3 space-y-4">
              {blocks.length > 0 && (
                <ActorTable kind="block" entries={blocks} onRemove={(e) => setConfirm({ id: e.id, label: e.actor_email || e.ip })}/>
              )}
              {watches.length > 0 && (
                <ActorTable kind="watch" entries={watches} onRemove={(e) => setConfirm({ id: e.id, label: e.actor_email || e.ip })}/>
              )}
            </div>
          )}
        </div>
      )}

      <BlockActorDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => load()}
      />

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="actor-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from list?</AlertDialogTitle>
            <AlertDialogDescription>
              This will unblock <span className="font-mono">{confirm?.label}</span> immediately. They&apos;ll be able to sign in and hit /api/* endpoints on their next request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="actor-remove-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRemove}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="actor-remove-confirm-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function ActorTable({ kind, entries, onRemove }) {
  const isBlock = kind === "block";
  return (
    <div data-testid={`actor-table-${kind}`}>
      <div className={`text-[10px] uppercase tracking-wide font-semibold mb-1.5 flex items-center gap-1 ${isBlock ? "text-rose-700" : "text-sky-700"}`}>
        {isBlock ? <ShieldOff size={11}/> : <Eye size={11}/>}
        {isBlock ? "Blocked" : "Watched"} ({entries.length})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-1.5 pr-3 font-medium">Actor</th>
              <th className="py-1.5 pr-3 font-medium">IP</th>
              <th className="py-1.5 pr-3 font-medium">Reason</th>
              <th className="py-1.5 pr-3 font-medium">Added by</th>
              <th className="py-1.5 pr-3 font-medium">Expires</th>
              <th className="py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`actor-row-${e.id}`}>
                <td className="py-1.5 pr-3 font-mono text-slate-800">{e.actor_email || <span className="text-slate-400 italic">—</span>}</td>
                <td className="py-1.5 pr-3 font-mono text-slate-800">{e.ip || <span className="text-slate-400 italic">—</span>}</td>
                <td className="py-1.5 pr-3 text-slate-700">{e.reason || <span className="text-slate-400 italic">—</span>}</td>
                <td className="py-1.5 pr-3 text-slate-600 font-mono">{e.added_by}</td>
                <td className="py-1.5 pr-3 text-slate-600">{e.expires_at ? new Date(e.expires_at).toLocaleString() : <span className="text-slate-400 italic">never</span>}</td>
                <td className="py-1.5">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => onRemove(e)}
                    className="h-6 px-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`actor-remove-${e.id}`}
                    title="Remove from list"
                  >
                    <Trash2 size={12}/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
